export type LandmarkId = "workshop" | "lighthouse" | "garden";
export type NpcId =
  | "flame"
  | "robot"
  | "drop"
  | "butterfly"
  | "hedgehog"
  | "balloon"
  | "fox"
  | "snail";

export type LandmarkDef = {
  id: LandmarkId;
  name: string;
  subtitle: string;
  postcardTitle: string;
  postcardBody: string;
  lat: number;
  lon: number;
};

export type NpcDef = {
  id: NpcId;
  name: string;
  title: string;
  lines: string[];
  badgeTitle: string;
  badgeBody: string;
  guideTo: LandmarkId;
  lat: number;
  lon: number;
};

export const LANDMARKS: LandmarkDef[] = [
  {
    id: "workshop",
    name: "策略工坊",
    subtitle: "把规则写成手艺",
    postcardTitle: "工坊夜班",
    postcardBody:
      "工作台上只留三样东西：什么时候进、什么时候出、你肯亏多少。其余都是闲话。Henry 把这张卡片夹进本子时，台上的灯还没关。",
    lat: 18,
    lon: 22,
  },
  {
    id: "lighthouse",
    name: "K线灯塔",
    subtitle: "雾里认岸，不认下一根",
    postcardTitle: "灯塔下的刻痕",
    postcardBody:
      "光不是为了告诉你下一根阳线在哪。它只保证：回撤来的时候，你还知道岸的方向。石头上有人把最大回撤刻得很浅，浅到要蹲下去才看得见。",
    lat: -10,
    lon: 138,
  },
  {
    id: "garden",
    name: "数据花园",
    subtitle: "样本会开花，也会过季",
    postcardTitle: "一季的花",
    postcardBody:
      "这里的东西按季节长。采的人若把一季的花当成土壤，明年会空手而归。Henry 只摘了三枚还带着露水的样本，并在背面写下日期。",
    lat: 34,
    lon: -88,
  },
];

export const NPCS: NpcDef[] = [
  {
    id: "flame",
    name: "动量小火人",
    title: "跑得快的窗口",
    lines: [
      "我跑得快，也经常跑过头。",
      "工坊里的人说，动量不是勇气，是一段还没被走完的窗口。要不要我带你去看看他们的台子？",
    ],
    badgeTitle: "动量纹章",
    badgeBody: "窗口还在的时候再跑。火熄了就坐下。",
    guideTo: "workshop",
    lat: 12,
    lon: 40,
  },
  {
    id: "robot",
    name: "风控机器人",
    title: "在手抖之前",
    lines: [
      "停损不是认输。我只负责在你手抖之前把手拿开。",
      "灯塔下面有人把规则焊在铁板上。我带你过去。",
    ],
    badgeTitle: "风控印记",
    badgeBody: "先写下不许做什么，再谈能做什么。",
    guideTo: "lighthouse",
    lat: -18,
    lon: 118,
  },
  {
    id: "drop",
    name: "周期水滴",
    title: "潮位",
    lines: [
      "潮来潮去。花园里的东西都按季节长。",
      "别在枯水期硬种。走，我带你去看现在开到哪一层。",
    ],
    badgeTitle: "周期水印",
    badgeBody: "先问现在是哪一季，再问今天涨了多少。",
    guideTo: "garden",
    lat: 28,
    lon: -60,
  },
  {
    id: "butterfly",
    name: "波动蝴蝶",
    title: "扇一下就换脸",
    lines: [
      "翅膀一扇，价格就换脸。",
      "别追着翅膀跑，看它停在哪根枝上。花园那边枝条多。",
    ],
    badgeTitle: "波动翼斑",
    badgeBody: "振幅是天气，不是性格。",
    guideTo: "garden",
    lat: 42,
    lon: -110,
  },
  {
    id: "hedgehog",
    name: "仓位刺猬",
    title: "只露出该露的",
    lines: [
      "刺向外，是因为我只露出该露的仓。",
      "满仓的人看起来勇敢，其实没处可退。工坊里有人专门管这件事。",
    ],
    badgeTitle: "仓位棘冠",
    badgeBody: "先留退路，再谈进攻。",
    guideTo: "workshop",
    lat: 4,
    lon: 8,
  },
  {
    id: "balloon",
    name: "情绪气球",
    title: "线在你手里",
    lines: [
      "我飞得高的时候，下面的人最容易跟着飘。",
      "线在你手里。灯塔的光比较沉，不容易被风带走。",
    ],
    badgeTitle: "情绪系绳",
    badgeBody: "热度可以借，仓位不能借。",
    guideTo: "lighthouse",
    lat: -2,
    lon: 78,
  },
  {
    id: "fox",
    name: "套利狐狸",
    title: "走中间那条",
    lines: [
      "两边价差像两条影子。",
      "我只走中间那条还没被踩实的路。工坊门口那条小径，现在还空着。",
    ],
    badgeTitle: "套利狐印",
    badgeBody: "价差不是故事，是还没被走完的缝。",
    guideTo: "workshop",
    lat: 22,
    lon: -20,
  },
  {
    id: "snail",
    name: "回撤蜗牛",
    title: "退，是为了还在路上",
    lines: [
      "退，是为了还在路上。",
      "灯塔下面有人把回撤刻在石头上。字很浅，你得蹲下去看。",
    ],
    badgeTitle: "回撤壳纹",
    badgeBody: "慢不是惩罚，是还活着的证据。",
    guideTo: "lighthouse",
    lat: -22,
    lon: 158,
  },
];

export function landmarkById(id: LandmarkId): LandmarkDef {
  const found = LANDMARKS.find((item) => item.id === id);
  if (!found) {
    throw new Error(`unknown landmark: ${id}`);
  }
  return found;
}

export function npcById(id: NpcId): NpcDef {
  const found = NPCS.find((item) => item.id === id);
  if (!found) {
    throw new Error(`unknown npc: ${id}`);
  }
  return found;
}
